import { useEffect, useRef } from "react";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { NodeTypes, type NodeData } from "@/workflow_api/types/workflow";

/**
 * A hook to synchronize the outputs of an Interoperability node based on its configuration.
 * This ensures that the node always has 'result' and 'error' outputs for canvas routing.
 */
export const useSyncInteroperabilityOutputs = (nodeId: string) => {
  const { getNodeById, setNodeConnections } = useWorkflowStore();
  const lastConnectionsRef = useRef<string | null>(null);

  const node = getNodeById(nodeId);
  const nodeData = node?.data as NodeData | undefined;

  useEffect(() => {
    if (!nodeData || nodeData.type !== NodeTypes.INTEROPERABILITY) {
      return;
    }

    const currentConnectionsStr = JSON.stringify(nodeData.connections);

    // Only run the logic if the connections have actually changed
    if (currentConnectionsStr === lastConnectionsRef.current) {
      return;
    }

    const defaultOutputs = {
      success: {
        display_name: "Resultado",
        target_node:
          nodeData.connections?.["result"]?.target_node ||
          nodeData.connections?.["success"]?.target_node || // Legacy support
          null,
      },
      error: {
        display_name: "Error",
        target_node: nodeData.connections?.["error"]?.target_node || null,
      },
    };

    const currentConnections = nodeData.connections || {};

    // Check if the essential 'result' and 'error' connections are missing or need updating
    const needsUpdate =
      !currentConnections["result"] ||
      !currentConnections["error"] ||
      (currentConnections["success"] && !currentConnections["result"]); // Handle legacy 'success' case

    if (needsUpdate) {
      setNodeConnections(nodeId, defaultOutputs);
    }

    // Update the ref to the current state to prevent re-running
    lastConnectionsRef.current = JSON.stringify(defaultOutputs);
  }, [nodeId, nodeData, setNodeConnections]);
};
